import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Clock } from "lucide-react";
import { Wrap, H2, P, Prose, Impact, Disclaimer, CheckList } from "@/components/Content";
import { Seo, articleSchema, breadcrumbSchema } from "@/components/Seo";
import { ARTICLES } from "@/data/articles";

const Block = ({ b }) => {
  switch (b.type) {
    case "h2":
      return <H2 className="pt-4">{b.text}</H2>;
    case "impact":
      return <Impact tone="holo">{b.text}</Impact>;
    case "list":
      return <CheckList items={b.items} />;
    case "disclaimer":
      return (
        <Disclaimer>
          <p>{b.text}</p>
        </Disclaimer>
      );
    default:
      return <P>{b.text}</P>;
  }
};

export default function LibraryArticle() {
  const { slug } = useParams();
  const a = ARTICLES[slug];
  if (!a) return <Navigate to="/library" replace />;

  const jsonLd = [
    articleSchema({
      title: a.title,
      description: a.metaDescription,
      path: a.path,
      datePublished: a.datePublished,
    }),
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Library", path: "/library" },
      { name: a.title, path: a.path },
    ]),
  ];

  return (
    <div className="bg-ink min-h-screen pb-24">
      <Seo title={`${a.title} | CardScience Library`} description={a.metaDescription} path={a.path} type="article" jsonLd={jsonLd} />

      <div className="relative border-b border-[color:var(--line)] overflow-hidden noise">
        <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(60% 100% at 50% 0%, rgba(36,224,255,0.22), transparent 60%)" }} />
        <div className="relative mx-auto max-w-[820px] px-5 py-16">
          <Link to="/library" className="inline-flex items-center gap-2 text-sm text-[color:var(--chrome-2)] hover:text-white mb-6">
            <ArrowLeft size={15} /> The Library
          </Link>
          <p className="eyebrow mb-4">{a.category}</p>
          <h1 className="h-display text-3xl sm:text-4xl lg:text-5xl text-chrome leading-[1.05]">{a.title}</h1>
          <div className="flex items-center gap-4 mt-6 text-xs text-[color:var(--chrome-3)]">
            <span className="flex items-center gap-2"><Clock size={13} /> {a.readTime}</span>
            <span>·</span>
            <span>{new Date(a.datePublished).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
          </div>
        </div>
      </div>

      <Wrap className="py-16 max-w-[820px]">
        <Prose>
          {a.body.map((b, i) => (
            <Block key={i} b={b} />
          ))}
        </Prose>

        <div className="flex flex-wrap gap-3 mt-14">
          <Link to="/submit" className="btn-holo inline-flex items-center gap-2 rounded-xl px-7 py-4 text-sm">
            SUBMIT A COLLECTIBLE <ArrowRight size={16} />
          </Link>
          {a.related?.map((r) => (
            <Link key={r.to} to={r.to} className="btn-ghost inline-flex items-center gap-2 rounded-xl px-7 py-4 text-sm">
              {r.label}
            </Link>
          ))}
        </div>
      </Wrap>
    </div>
  );
}
